import { FundriseRouteConfig, RouteLocationRaw } from 'vue-router';
import { checkAndSwitchToIraEntity } from './ira-navigation-guards';
import { useAppStore } from '@stores/app';
import { useInvestmentEntityStore } from '@stores/investment-entity';
import { useIraStore } from '@stores/ira';
import { useSignupStore } from '@stores/signup';
import { useUserStore } from '@stores/user';
const getCompletedIraId = (): string | null => {
	const userStore = useUserStore();
	return (
		userStore.user?.entityDetails?.investmentEntities.find(
			(entity) => entity.entityType === 'IRA' && entity.iraEntityMetadata?.isIraOnboardingFinished
		)?.investmentEntityId ?? null
	);
};

const getAnyIncompleteIraId = (): string | null => {
	const userStore = useUserStore();
	return (
		userStore.user?.entityDetails?.investmentEntities.find(
			(entity) => entity.entityType === 'IRA' && !entity.iraEntityMetadata?.isIraOnboardingFinished
		)?.investmentEntityId ?? null
	);
};

const updateIraEntityForRolloverRedirect = async (
	anyCompletedIraId: string | null,
	next?: (to?: RouteLocationRaw) => void
): Promise<void> => {
	const anyIncompleteIraId = getAnyIncompleteIraId();
	const investmentEntityStore = useInvestmentEntityStore();
	const userStore = useUserStore();

	const hasCompletedIraNotSelected =
		anyCompletedIraId !== null && investmentEntityStore.investmentEntityId !== anyCompletedIraId;

	const hasIncompleteIraNotSelected =
		anyIncompleteIraId !== null && investmentEntityStore.investmentEntityId !== anyIncompleteIraId;

	if (hasCompletedIraNotSelected) {
		await userStore.updateSelectedEntityWithoutReload({
			investmentEntityId: anyCompletedIraId ?? '',
			nextRoute: {
				route: {
					name: 'ira-transfer-reminder-redirect'
				},
				next
			}
		});
	} else if (anyIncompleteIraId !== null) {
		if (hasIncompleteIraNotSelected) {
			await userStore.updateSelectedEntity(anyIncompleteIraId);
		}
		const ira = useIraStore();
		await ira.resetIraForSimpleIraReminderRedirect();
	}
};

const iraGeneralRoutes: FundriseRouteConfig[] = [
	{
		path: `/ira/rollover/checklist`,
		name: 'ira-rollover-checklist',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: checkAndSwitchToIraEntity,
		component: () => import('@views/ira/general/ira-rollover-checklist.vue')
	},
	{
		path: `/ira/checkout`,
		name: 'ira-checkout-individual-details',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			const appStore = useAppStore();
			const signupStore = useSignupStore();
			const userStore = useUserStore();
			if (appStore.isAuthenticated) {
				await signupStore.getUserCheckoutDetailsData();
			}

			if (userStore.hasIraEntity) {
				next({ name: 'ira-start', replace: true });
			} else if (signupStore.isIraCheckout) {
				next();
			} else {
				next({ name: 'checkout-resume' });
			}
		},
		component: () => import('@views/ira/general/ira-checkout-individual-account-details.vue')
	},
	{
		path: `/ira`,
		name: 'ira-landing',
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		beforeEnter: async (to, from, next) => {
			const ira = useIraStore();
			await ira.getIraFeesAndContributionLimits();
			next();
		},
		component: () => import('@views/ira/general/ira-landing.vue')
	},
	{
		path: `/mtc`,
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'ira-landing' }
	},
	{
		path: `/IRA`,
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'ira-landing' }
	},
	{
		path: `/ira/signup`,
		meta: { minTokenScopeRequired: 'UNAUTHENTICATED' },
		redirect: { name: 'ira-start' }
	},
	{
		path: `/ira/email-saved`,
		name: 'ira-email-saved',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			const appStore = useAppStore();
			next({ name: 'account-overview', replace: true });
			appStore.addToast({
				type: 'success',
				message: 'We will email you when your Simple IRA account is eligible for transfer.'
			});
		},
		// revisit using redirect
		component: () => import('@views/ira/general/ira-landing.vue')
	},
	{
		path: `/ira/transfer/reminder/redirect`,
		name: 'ira-transfer-reminder-redirect',
		meta: { minTokenScopeRequired: 'FULL_ACCESS' },
		beforeEnter: async (to, from, next) => {
			const userStore = useUserStore();
			if (userStore.iraEntities.length > 0) {
				const completedIraId = getCompletedIraId();
				await updateIraEntityForRolloverRedirect(completedIraId);
				const redirect = completedIraId ? 'ira-add-funds-start' : 'ira-start';
				next({ name: redirect, replace: true });
			} else {
				next({ name: 'account-overview' });
			}
		},
		// revisit using redirect
		component: () => import('@views/ira/general/ira-landing.vue')
	}
];

export default iraGeneralRoutes;
